// Draws the playfield at its own resolution, W x 448 with one world unit per pixel, and lets CSS
// scale it up pixelated to fill the window. The field is plain black: only bullets, enemies and
// the ship draw. Sprites go through WebGL2: one atlas, one instanced draw call a frame (Canvas 2D
// topped out around 3k sprites in testing). Kinds whose sprite is missing (a fresh clone has none,
// since the art is gitignored) get a generated circle instead.

import type { Stuck } from './dodge.ts';
import { PLAYER_HIT } from './hits.ts';
import {
  compositions,
  effects,
  isEnemy,
  type KindSpec,
  kindSpec,
  kinds,
  type LaserSpec,
  laserKinds,
  laserSpec,
  ship,
  spriteFiles,
} from './kinds.ts';
import { type Bullets, FIELD, type Lasers } from './sim.ts';

/** A rectangle in the atlas, in pixels. */
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A kind resolved against the atlas. */
interface Look {
  frames: Rect[];
  fps: number;
  angle: number | null;
  spin: number;
  hidden: boolean;
}

interface Kind {
  enemy: boolean;
  look: Look;
}

interface Effect {
  look: Look;
  x: number;
  y: number;
  /** Render frame the effect started on. */
  start: number;
}

/** A cancelled bullet turned into an item: it floats up and fades. Host-side only. */
interface Item {
  look: Look;
  x: number;
  y: number;
  start: number;
}

const MAX_EFFECTS = 400;
const MAX_ITEMS = 800;
/** Frames an item lives, and field units it rises in that time. */
const ITEM_LIFE = 50;
const ITEM_RISE = 24;
/** The telegraph ring's radius at its widest, in field units. */
const TELEGRAPH_RADIUS = 14;
/** Hit circles are drawn for whole radii up to this; bigger ones use the biggest. */
const MAX_HIT_RING = 32;
/** Laser hit bands get a slice per whole width up to this, so the edges stay one unit wide. */
const MAX_HIT_BAND = 64;
const CAN_HIT = 4;
const HIT_COLOR = '#ffffff';
const NO_TINT: [number, number, number] = [1, 1, 1];

/** Ticks a hit flash lasts. */
export const HIT_FLASH = 30;

/** What the page asks the renderer to show on top of the game. */
export interface Overlay {
  /** Draw hit circles and laser hit widths. */
  hitboxes: boolean;
  /** Hit radius by kind id. */
  hitRadius: (kind: number) => number;
  /** Ticks of hit flash left on the ship; 0 for none. */
  flash: number;
  /** The dodge bot's run while it plays back; the ship drawn is the bot's. */
  bot: BotOverlay | null;
}

export interface BotOverlay {
  /** Where the bot stood after each tick of its run. */
  xs: Float64Array;
  ys: Float64Array;
  /** The tick of its run on screen. */
  at: number;
  stuck: Stuck[];
}

/** Ticks of the bot's path the trail shows behind it. */
const TRAIL = 90;
/** Stuck markers show this many ticks either side of the stretch. */
const STUCK_LEAD = 40;
const TRAIL_RGB: [number, number, number] = [0.45, 0.85, 1];
/** Per sprite: centre (2), rotation (2), atlas rect (4), alpha (1). */
const FLOATS = 9;
/** Per laser vertex: position (2), across/along (2), atlas rect (4), colour (4). */
const LASER_FLOATS = 12;
const ATLAS_WIDTH = 2048;
/** Miter joints longer than this many half-widths get clipped, so sharp turns don't spike. */
const MITER_LIMIT = 2.5;

const VERTEX = `#version 300 es
layout(location = 0) in vec2 a_center;
layout(location = 1) in vec2 a_rot;
layout(location = 2) in vec4 a_rect;
layout(location = 3) in float a_alpha;
uniform vec2 u_view;
uniform vec2 u_atlas;
out vec2 v_uv;
out float v_alpha;
void main() {
  vec2 corner = vec2(float(gl_VertexID & 1), float(gl_VertexID >> 1));
  vec2 local = (corner - 0.5) * a_rect.zw;
  vec2 p = a_center + vec2(a_rot.x * local.x - a_rot.y * local.y, a_rot.y * local.x + a_rot.x * local.y);
  vec2 clip = p / u_view * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  v_uv = (a_rect.xy + corner * a_rect.zw) / u_atlas;
  v_alpha = a_alpha;
}`;

const FRAGMENT = `#version 300 es
precision mediump float;
uniform sampler2D u_tex;
in vec2 v_uv;
in float v_alpha;
out vec4 color;
void main() {
  color = texture(u_tex, v_uv) * v_alpha;
}`;

// Lasers are ribbons: a beam slice from the atlas, stretched across the width and tiled along
// the length. The atlas can't wrap a sub-rectangle, so the tiling happens here.
const LASER_VERTEX = `#version 300 es
layout(location = 0) in vec2 a_pos;
layout(location = 1) in vec2 a_uv;
layout(location = 2) in vec4 a_rect;
layout(location = 3) in vec4 a_color;
uniform vec2 u_view;
out vec2 v_uv;
flat out vec4 v_rect;
out vec4 v_color;
void main() {
  vec2 clip = a_pos / u_view * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  v_uv = a_uv;
  v_rect = a_rect;
  v_color = a_color;
}`;

const LASER_FRAGMENT = `#version 300 es
precision highp float;
uniform sampler2D u_tex;
uniform vec2 u_atlas;
in vec2 v_uv;
flat in vec4 v_rect;
in vec4 v_color;
out vec4 color;
void main() {
  vec2 texel = v_rect.xy + vec2(clamp(v_uv.x, 0.0, 0.999) * v_rect.z, mod(v_uv.y, v_rect.w));
  color = texture(u_tex, texel / u_atlas) * vec4(v_color.rgb, 1.0) * v_color.a;
}`;

/** A laser kind resolved against the atlas. */
interface LaserLook {
  spec: LaserSpec;
  /** Beam body per frame, or the generated solid beam when the strip is missing. */
  frames: Rect[];
  /** Muzzle and hit flame frames; empty without sprites. */
  muzzle: Rect[];
  hit: Rect[];
  /** Solid colour as straight RGB, 0 to 1. */
  rgb: [number, number, number];
}

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  /** Called the first time a kind name kinds.ts doesn't know shows up after a resetKinds(). */
  onUnknownKind: (name: string) => void = () => {};
  /** Goes up whenever the atlas is rebuilt, e.g. once the sprites have loaded. */
  atlasVersion = 0;

  private gl: WebGL2RenderingContext;
  private program!: WebGLProgram;
  private buffer!: WebGLBuffer;
  private spriteVao!: WebGLVertexArrayObject;
  private laserProgram!: WebGLProgram;
  private laserBuffer!: WebGLBuffer;
  private laserVao!: WebGLVertexArrayObject;
  private laserView: WebGLUniformLocation | null = null;
  private laserAtlas: WebGLUniformLocation | null = null;
  private texture!: WebGLTexture;
  private uView: WebGLUniformLocation | null = null;
  private uAtlas: WebGLUniformLocation | null = null;
  private atlasSize = { w: 1, h: 1 };
  private atlas: HTMLCanvasElement | null = null;

  private images = new Map<string, HTMLImageElement>();
  private rects = new Map<string, Rect>();
  private kinds: (Kind | undefined)[] = [];
  private laserKinds: (LaserLook | undefined)[] = [];
  private white!: Rect;
  private buckets = new Map<Kind, number[]>();
  private shipLook!: Look;
  private hitbox!: Rect;
  private effectLooks!: { hit: Look; explosion: Look };
  private fx: Effect[] = [];
  private items: Item[] = [];
  private itemLooks!: Look[];
  /** Telegraph rings by radius, so each size is a crisp one-pixel ring. */
  private telegraphs: Rect[] = [];
  /** Hit circles by radius, for the hitbox overlay. */
  private hitRings: Rect[] = [];
  private hitBands: Rect[] = [];
  private stuckMark!: Rect;
  private stuckRing!: Rect;
  private trail = new Float32Array(0);
  private playerRing: Rect | null = null;
  private data = new Float32Array(FLOATS * 4096);
  private count = 0;
  /** Sprites before this index draw under the lasers, the rest over them. */
  private under = 0;
  private laserData = new Float32Array(LASER_FLOATS * 6 * 1024);
  private laserVerts = 0;
  /** Scratch for one laser's joint offsets, reused across frames. */
  private joints = new Float32Array(4 * 128);
  private frame = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    canvas.width = FIELD.w;
    canvas.height = FIELD.h;
    const gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: false });
    if (!gl) throw new Error('This browser has no WebGL2, which the playfield needs.');
    this.gl = gl;
    this.setup();
    this.buildAtlas();
    canvas.addEventListener('webglcontextlost', (e) => e.preventDefault());
    canvas.addEventListener('webglcontextrestored', () => {
      this.setup();
      this.buildAtlas();
    });
  }

  /** Loads whatever `npm run sprites` copied, then rebuilds the atlas with them. */
  async loadSprites(): Promise<void> {
    let available: Set<string>;
    try {
      const res = await fetch('sprites/manifest.json');
      if (!res.ok) return;
      available = new Set((await res.json()).files as string[]);
    } catch {
      return;
    }
    await Promise.all(
      spriteFiles()
        .filter((f) => available.has(f))
        .map(async (file) => {
          const img = new Image();
          img.src = `sprites/${file}`;
          try {
            await img.decode();
            this.images.set(file, img);
          } catch {
            // Leave it out; that kind gets a generated circle.
          }
        }),
    );
    this.buildAtlas();
  }

  /** Symbol ids belong to one VM, so forget the mapping when the VM is replaced. */
  resetKinds(): void {
    this.kinds = [];
    this.laserKinds = [];
    this.buckets.clear();
  }

  /**
   * Matches the canvas to the field's current size and scales it up to the largest size that
   * fits the box (CSS pixels) without changing its shape. Any scale goes: the pixels just get
   * bigger.
   */
  fit(cssWidth: number, cssHeight: number): void {
    if (this.canvas.width !== FIELD.w) this.canvas.width = FIELD.w;
    if (this.canvas.height !== FIELD.h) this.canvas.height = FIELD.h;
    const scale = Math.min(cssWidth / FIELD.w, cssHeight / FIELD.h);
    this.canvas.style.width = `${FIELD.w * scale}px`;
    this.canvas.style.height = `${FIELD.h * scale}px`;
  }

  /** Converts a pointer position to world coordinates. */
  toWorld(clientX: number, clientY: number): { x: number; y: number } {
    const r = this.canvas.getBoundingClientRect();
    return {
      x: ((clientX - r.left) / r.width) * FIELD.w,
      y: FIELD.h - ((clientY - r.top) / r.height) * FIELD.h,
    };
  }

  /** A kind's first frame as the field draws it, one pixel per unit. Lasers show a slice of beam. */
  thumbnail(name: string): HTMLCanvasElement {
    let rect: Rect;
    if (laserKinds[name]) {
      const body = this.resolveLaser(laserSpec(name)).frames[0];
      rect = { ...body, h: Math.min(body.h, 40) };
    } else {
      const spec = kindSpec(name);
      rect = this.resolve(spec, circleKey(spec)).frames[0] ?? { x: 0, y: 0, w: 1, h: 1 };
    }
    const [c, g] = canvas2d(rect.w, rect.h);
    if (this.atlas) g.drawImage(this.atlas, rect.x, rect.y, rect.w, rect.h, 0, 0, rect.w, rect.h);
    return c;
  }

  /** Turns a cancelled bullet into an item: a coin for small bullets, a gem for big ones. */
  item(x: number, y: number, kind: string): void {
    if (this.items.length >= MAX_ITEMS) return;
    const big = /^(big|huge|orb|ring)/.test(kind);
    this.items.push({ look: this.itemLooks[big ? 1 : 0], x, y, start: this.frame });
  }

  /** Plays a hit spark or an explosion where an actor left the field. */
  burst(x: number, y: number, big: boolean): void {
    if (this.fx.length >= MAX_EFFECTS) return;
    const look = big ? this.effectLooks.explosion : this.effectLooks.hit;
    this.fx.push({ look, x, y, start: this.frame });
  }

  draw(
    view: Bullets,
    lasers: Lasers,
    lookup: (kind: number) => string,
    player: { x: number; y: number; vx: number },
    overlay: Overlay,
  ): void {
    this.frame++;
    this.count = 0;
    this.laserVerts = 0;

    for (const list of this.buckets.values()) list.length = 0;
    for (let i = 0; i < view.count; i++) {
      const kind = this.kind(view.kind[i], lookup);
      if (kind.look.hidden) continue;
      let list = this.buckets.get(kind);
      if (!list) {
        list = [];
        this.buckets.set(kind, list);
      }
      list.push(i);
    }

    // Enemies under the ship, then lasers, bullets over both, and the hitbox over everything:
    // it's what dodges.
    for (const [kind, list] of this.buckets) if (kind.enemy) this.pushKind(kind.look, list, view);
    this.pushShip(player, overlay.flash, overlay.bot !== null);
    this.pushEffects();
    this.pushItems();
    this.under = this.count;
    this.pushLasers(lasers, lookup);
    for (const [kind, list] of this.buckets) if (!kind.enemy) this.pushKind(kind.look, list, view);
    if (overlay.hitboxes) this.pushHitboxes(view, lasers, player, overlay);
    this.pushHitRing(player, overlay.flash);
    if (overlay.bot) this.pushBot(overlay.bot);
    this.push(this.hitbox, player.x, FIELD.h - player.y, 1, 0);

    this.flush();
  }

  private laserLook(id: number, lookup: (kind: number) => string): LaserLook {
    let look = this.laserKinds[id];
    if (!look) {
      const name = lookup(id);
      if (!laserKinds[name]) this.onUnknownKind(name);
      look = this.resolveLaser(laserSpec(name));
      this.laserKinds[id] = look;
    }
    return look;
  }

  /**
   * Builds every laser's ribbon, and queues its muzzle and hit flames as sprites.
   * Warn is a thin flickering line, active the full textured beam, fade a thinning, dimming beam.
   */
  private pushLasers(lv: Lasers, lookup: (kind: number) => string): void {
    for (let i = 0; i < lv.count; i++) {
      const look = this.laserLook(lv.kind[i], lookup);
      const phase = lv.phase[i];
      const t = lv.progress[i];
      const full = lv.width[i];
      let width: number;
      let alpha: number;
      let rect: Rect;
      let rgb: [number, number, number] = [1, 1, 1];
      if (phase === 0) {
        // The telegraph: thin, low alpha, flickering a little faster as it's about to fire.
        width = 1.5;
        const rate = t > 0.7 ? 2 : 4;
        alpha = Math.floor(this.frame / rate) % 2 === 0 ? 0.55 : 0.25;
        rect = this.white;
        rgb = look.rgb;
      } else {
        width = phase === 2 ? full * (1 - t) : full;
        alpha = phase === 2 ? 1 - t : 1;
        const n = look.frames.length;
        rect = look.frames[Math.floor((this.frame * look.spec.fps) / 60) % n];
      }
      if (width < 0.5 || alpha <= 0.01) continue;
      // Texels per field unit along the beam, from the full width so the pattern holds still
      // while the beam thins out.
      const scale = rect === this.white ? 1 : rect.w / Math.max(full, 1);
      const flow = -this.frame * look.spec.flow;
      const start = lv.pointsStart[i];
      const len = lv.pointsLen[i];
      this.ribbon(lv.points, start, len, width, scale, flow, rect, rgb, alpha);
      if (phase === 1) this.pushFlames(lv.points, start, len, look, full);
    }
  }

  /** One polyline as a strip of quads with mitred joints, appended to the laser vertices. */
  private ribbon(
    pts: Float32Array,
    start: number,
    len: number,
    width: number,
    scale: number,
    flow: number,
    rect: Rect,
    rgb: [number, number, number],
    alpha: number,
  ): void {
    if (len < 2) return;
    if (this.joints.length < len * 4) this.joints = new Float32Array(len * 8);
    const j = this.joints;
    const half = width / 2;
    const H = FIELD.h;
    // Offsets of each joint's left edge from its centre, and its distance along the beam.
    let along = 0;
    for (let k = 0; k < len; k++) {
      const x = pts[(start + k) * 2];
      const y = H - pts[(start + k) * 2 + 1];
      let nx = 0;
      let ny = 0;
      let count = 0;
      let first = { x: 0, y: 0 };
      for (const step of [-1, 1]) {
        const m = k + step;
        if (m < 0 || m >= len) continue;
        const ox = pts[(start + m) * 2];
        const oy = H - pts[(start + m) * 2 + 1];
        let dx = step > 0 ? ox - x : x - ox;
        let dy = step > 0 ? oy - y : y - oy;
        const d = Math.hypot(dx, dy);
        if (d < 1e-6) continue;
        dx /= d;
        dy /= d;
        if (count === 0) first = { x: -dy, y: dx };
        nx += -dy;
        ny += dx;
        count++;
      }
      if (count === 0) {
        nx = 0;
        ny = 1;
      }
      const nl = Math.hypot(nx, ny);
      let ox = 0;
      let oy = half;
      if (nl > 1e-6) {
        nx /= nl;
        ny /= nl;
        // Stretch the joint so the edges stay parallel to both segments, within the limit.
        const cos = count === 2 ? Math.max(nx * first.x + ny * first.y, 1 / MITER_LIMIT) : 1;
        ox = (nx * half) / cos;
        oy = (ny * half) / cos;
      }
      if (k > 0) {
        along += Math.hypot(x - pts[(start + k - 1) * 2], y - (H - pts[(start + k - 1) * 2 + 1]));
      }
      j[k * 4] = ox;
      j[k * 4 + 1] = oy;
      j[k * 4 + 2] = along * scale + flow;
      j[k * 4 + 3] = 0;
    }

    const need = (this.laserVerts + (len - 1) * 6) * LASER_FLOATS;
    if (need > this.laserData.length) {
      const bigger = new Float32Array(Math.max(need, this.laserData.length * 2));
      bigger.set(this.laserData);
      this.laserData = bigger;
    }
    const vertex = (k: number, side: number) => {
      const o = this.laserVerts * LASER_FLOATS;
      const d = this.laserData;
      d[o] = pts[(start + k) * 2] + side * j[k * 4];
      d[o + 1] = H - pts[(start + k) * 2 + 1] + side * j[k * 4 + 1];
      d[o + 2] = side > 0 ? 0 : 1;
      d[o + 3] = j[k * 4 + 2];
      d[o + 4] = rect.x;
      d[o + 5] = rect.y;
      d[o + 6] = rect.w;
      d[o + 7] = rect.h;
      d[o + 8] = rgb[0];
      d[o + 9] = rgb[1];
      d[o + 10] = rgb[2];
      d[o + 11] = alpha;
      this.laserVerts++;
    };
    for (let k = 0; k < len - 1; k++) {
      vertex(k, 1);
      vertex(k, -1);
      vertex(k + 1, 1);
      vertex(k, -1);
      vertex(k + 1, -1);
      vertex(k + 1, 1);
    }
  }

  /** A muzzle flame at the origin (the tail of a curvy laser) and an impact at the tip. */
  private pushFlames(pts: Float32Array, start: number, len: number, look: LaserLook, width: number): void {
    const H = FIELD.h;
    const n = look.muzzle.length;
    if (n === 0 || len < 2) return;
    const at = (k: number) => ({ x: pts[(start + k) * 2], y: H - pts[(start + k) * 2 + 1] });
    const ends: [{ x: number; y: number }, { x: number; y: number }, Rect[]][] = [
      [at(0), at(1), look.muzzle],
      [at(len - 1), at(len - 2), look.hit],
    ];
    for (const [p, toward, frames] of ends) {
      if (frames.length === 0) continue;
      const rect = frames[Math.floor(this.frame / 4) % frames.length];
      // The flames point up as drawn; turn them to face away from the beam.
      const a = Math.atan2(p.y - toward.y, p.x - toward.x) + Math.PI / 2;
      const k = Math.min(1, (width * 1.4) / rect.w);
      this.push(rect, p.x, p.y, Math.cos(a) * k, Math.sin(a) * k, 1, false);
    }
  }

  private kind(id: number, lookup: (kind: number) => string): Kind {
    let kind = this.kinds[id];
    if (!kind) {
      const name = lookup(id);
      if (!kinds[name]) this.onUnknownKind(name);
      const spec = kindSpec(name);
      kind = { enemy: isEnemy(name), look: this.resolve(spec, circleKey(spec)) };
      this.kinds[id] = kind;
    }
    return kind;
  }

  private pushKind(look: Look, list: number[], view: Bullets): void {
    const n = look.frames.length;
    const rotates = look.angle !== null || look.spin !== 0;
    for (const i of list) {
      const rect = look.fps > 0 ? look.frames[Math.floor((view.age[i] * look.fps) / 60) % n] : look.frames[0];
      const x = view.x[i];
      const y = FIELD.h - view.y[i];
      const delay = view.delay[i];
      if (delay > 0) {
        // A bullet that can't hit yet shows as a telegraph: a ring closing in on a faint,
        // pulsing ghost of the bullet. It lands as the delay runs out.
        const pulse = 0.3 + 0.15 * Math.sin((this.frame + i) * 0.4);
        this.push(rect, x, y, 1, 0, pulse);
        const r = Math.round(3 + Math.min(delay, 30) * ((TELEGRAPH_RADIUS - 3) / 30));
        this.push(this.telegraphs[r] ?? this.white, x, y, 1, 0, 0.6);
        continue;
      }
      if (rotates) {
        const deg = (look.angle !== null ? view.heading[i] - look.angle : 0) + view.age[i] * look.spin;
        const a = (-deg * Math.PI) / 180;
        this.push(rect, x, y, Math.cos(a), Math.sin(a));
      } else {
        this.push(rect, x, y, 1, 0);
      }
    }
  }

  private pushShip(player: { x: number; y: number; vx: number }, flash: number, ghost: boolean): void {
    // Frame 2 is level flight; 0-1 bank left, 3-4 bank right.
    const frames = this.shipLook.frames;
    const bank = frames.length === 5 ? Math.max(-2, Math.min(2, Math.round(player.vx / 1.5))) + 2 : 0;
    // Just hit: blink, the way a shmup ship does while it's invulnerable.
    const alpha = flash > 0 && Math.floor(this.frame / 3) % 2 === 0 ? 0.2 : ghost ? 0.6 : 1;
    this.push(frames[bank], player.x, FIELD.h - player.y, 1, 0, alpha);
  }

  /**
   * The bot's trail behind it, under the bullets, and around the ticks it found no safe move,
   * blinking red rings where its paths stood and one around the ship while it's being hit.
   */
  private pushBot(bot: BotOverlay): void {
    const from = Math.max(0, bot.at - TRAIL);
    const n = bot.at - from + 1;
    if (this.trail.length < n * 2) this.trail = new Float32Array(n * 2);
    for (let k = 0; k < n; k++) {
      this.trail[k * 2] = bot.xs[from + k];
      this.trail[k * 2 + 1] = bot.ys[from + k];
    }
    this.ribbon(this.trail, 0, n, 1.5, 1, 0, this.white, TRAIL_RGB, 0.35);

    const H = FIELD.h;
    const blink = Math.floor(this.frame / 4) % 2 === 0 ? 1 : 0.35;
    for (const s of bot.stuck) {
      if (bot.at < s.from - STUCK_LEAD || bot.at > s.to + STUCK_LEAD) continue;
      for (const [x, y] of s.positions) this.push(this.stuckMark, x, H - y, 1, 0, blink);
      if (bot.at >= s.from && bot.at <= s.to) {
        this.push(this.stuckRing, bot.xs[bot.at], H - bot.ys[bot.at], 1, 0, blink);
      }
    }
  }

  /** A ring that opens out from the ship over the first few ticks of a hit, over the bullets. */
  private pushHitRing(player: { x: number; y: number }, flash: number): void {
    const t = HIT_FLASH - flash;
    if (flash <= 0 || t > TELEGRAPH_RADIUS - 4) return;
    this.push(this.telegraphs[t + 4], player.x, FIELD.h - player.y, 1, 0, 1 - t / (TELEGRAPH_RADIUS - 3));
  }

  /** Hit circles on every bullet that can hit, hit bands on lasers, and the ship's own circle. */
  private pushHitboxes(view: Bullets, lasers: Lasers, player: { x: number; y: number }, overlay: Overlay): void {
    const H = FIELD.h;
    for (let i = 0; i < view.count; i++) {
      if ((view.flags[i] & CAN_HIT) === 0) continue;
      const r = Math.min(MAX_HIT_RING, Math.max(1, Math.round(overlay.hitRadius(view.kind[i]))));
      this.push(this.hitRings[r] ?? this.white, view.x[i], H - view.y[i], 1, 0, 0.9);
    }
    for (let i = 0; i < lasers.count; i++) {
      if ((lasers.flags[i] & CAN_HIT) === 0) continue;
      const width = lasers.width[i];
      const band = this.hitBands[Math.min(MAX_HIT_BAND, Math.max(2, Math.round(width)))] ?? this.white;
      this.ribbon(lasers.points, lasers.pointsStart[i], lasers.pointsLen[i], width, 1, 0, band, NO_TINT, 1);
    }
    if (this.playerRing) this.push(this.playerRing, player.x, H - player.y, 1, 0);
  }

  private pushItems(): void {
    let keep = 0;
    for (const it of this.items) {
      const age = this.frame - it.start;
      if (age >= ITEM_LIFE) continue;
      this.items[keep++] = it;
      const t = age / ITEM_LIFE;
      const rect = it.look.frames[Math.floor((age * Math.max(it.look.fps, 1)) / 60) % it.look.frames.length];
      // Rise fast then slow, and fade over the last half.
      const rise = ITEM_RISE * (1 - (1 - t) * (1 - t));
      this.push(rect, it.x, FIELD.h - it.y - rise, 1, 0, t < 0.5 ? 1 : 2 * (1 - t));
    }
    this.items.length = keep;
  }

  private pushEffects(): void {
    let keep = 0;
    for (const fx of this.fx) {
      const index = Math.floor(((this.frame - fx.start) * fx.look.fps) / 60);
      if (index >= fx.look.frames.length) continue;
      this.fx[keep++] = fx;
      this.push(fx.look.frames[index], fx.x, FIELD.h - fx.y, 1, 0);
    }
    this.fx.length = keep;
  }

  /**
   * Queues one sprite centred at (x, y) in field pixels, y down, rotated by (c, sn) = (cos, sin).
   * The centre snaps so the sprite's corner lands on a whole pixel, which keeps it crisp.
   */
  private push(r: Rect, x: number, y: number, c: number, sn: number, alpha = 1, snap = true): void {
    if ((this.count + 1) * FLOATS > this.data.length) {
      const bigger = new Float32Array(this.data.length * 2);
      bigger.set(this.data);
      this.data = bigger;
    }
    const d = this.data;
    const o = this.count * FLOATS;
    d[o] = snap ? Math.round(x - r.w / 2) + r.w / 2 : x;
    d[o + 1] = snap ? Math.round(y - r.h / 2) + r.h / 2 : y;
    d[o + 2] = c;
    d[o + 3] = sn;
    d[o + 4] = r.x;
    d[o + 5] = r.y;
    d[o + 6] = r.w;
    d[o + 7] = r.h;
    d[o + 8] = alpha;
    this.count++;
  }

  private flush(): void {
    const gl = this.gl;
    gl.viewport(0, 0, FIELD.w, FIELD.h);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);

    gl.useProgram(this.program);
    gl.uniform2f(this.uView, FIELD.w, FIELD.h);
    gl.uniform2f(this.uAtlas, this.atlasSize.w, this.atlasSize.h);
    gl.bindVertexArray(this.spriteVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.data.subarray(0, this.count * FLOATS), gl.STREAM_DRAW);
    if (this.under > 0) {
      this.pointSprites(0);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.under);
    }

    if (this.laserVerts > 0) {
      gl.useProgram(this.laserProgram);
      gl.uniform2f(this.laserView, FIELD.w, FIELD.h);
      gl.uniform2f(this.laserAtlas, this.atlasSize.w, this.atlasSize.h);
      gl.bindVertexArray(this.laserVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.laserBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.laserData.subarray(0, this.laserVerts * LASER_FLOATS), gl.STREAM_DRAW);
      gl.drawArrays(gl.TRIANGLES, 0, this.laserVerts);
      gl.useProgram(this.program);
      gl.bindVertexArray(this.spriteVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    }

    if (this.count > this.under) {
      this.pointSprites(this.under);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.count - this.under);
    }
  }

  /** Points the sprite attributes at instance `first`, since WebGL2 has no base instance. */
  private pointSprites(first: number): void {
    const gl = this.gl;
    const stride = FLOATS * 4;
    const base = first * stride;
    const attribs: [number, number, number][] = [
      [0, 2, 0],
      [1, 2, 8],
      [2, 4, 16],
      [3, 1, 32],
    ];
    for (const [loc, size, offset] of attribs) {
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, base + offset);
    }
  }

  private setup(): void {
    const gl = this.gl;
    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type);
      if (!sh) throw new Error('WebGL could not create a shader');
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(sh) ?? 'shader failed to compile');
      }
      return sh;
    };
    const link = (vertex: string, fragment: string) => {
      const program = gl.createProgram();
      gl.attachShader(program, compile(gl.VERTEX_SHADER, vertex));
      gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fragment));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) ?? 'shaders failed to link');
      }
      return program;
    };
    this.program = link(VERTEX, FRAGMENT);
    this.uView = gl.getUniformLocation(this.program, 'u_view');
    this.uAtlas = gl.getUniformLocation(this.program, 'u_atlas');
    this.laserProgram = link(LASER_VERTEX, LASER_FRAGMENT);
    this.laserView = gl.getUniformLocation(this.laserProgram, 'u_view');
    this.laserAtlas = gl.getUniformLocation(this.laserProgram, 'u_atlas');

    this.spriteVao = gl.createVertexArray();
    gl.bindVertexArray(this.spriteVao);
    this.buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    for (const loc of [0, 1, 2, 3]) {
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribDivisor(loc, 1);
    }
    this.pointSprites(0);

    this.laserVao = gl.createVertexArray();
    gl.bindVertexArray(this.laserVao);
    this.laserBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.laserBuffer);
    const stride = LASER_FLOATS * 4;
    const laserAttribs: [number, number, number][] = [
      [0, 2, 0],
      [1, 2, 8],
      [2, 4, 16],
      [3, 4, 32],
    ];
    for (const [loc, size, offset] of laserAttribs) {
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, offset);
    }
    gl.bindVertexArray(this.spriteVao);

    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  }

  /**
   * Packs every loaded sprite, the composed frames and the generated stand-ins into one texture,
   * shelf by shelf. Sprites a kind draws smaller than 1:1 (the bosses) get a smoothly shrunk
   * copy, so they look like pixel art at field resolution instead of dropping every other pixel.
   */
  private buildAtlas(): void {
    const sources = new Map<string, HTMLImageElement | HTMLCanvasElement>(this.images);
    for (const [key, c] of composeFrames(this.images)) sources.set(key, c);
    for (const [key, c] of shrunkCopies(sources)) sources.set(key, c);
    for (const [key, c] of standIns()) sources.set(key, c);
    const items = [...sources].map(([key, source]) => ({ key, source, w: source.width, h: source.height }));
    items.sort((a, b) => b.h - a.h);

    this.rects.clear();
    let x = 0;
    let y = 0;
    let shelf = 0;
    for (const it of items) {
      if (x + it.w > ATLAS_WIDTH) {
        x = 0;
        y += shelf + 1;
        shelf = 0;
      }
      this.rects.set(it.key, { x, y, w: it.w, h: it.h });
      x += it.w + 1;
      shelf = Math.max(shelf, it.h);
    }

    const [atlas, g] = canvas2d(ATLAS_WIDTH, Math.max(1, y + shelf));
    for (const it of items) {
      const r = this.rects.get(it.key);
      if (r) g.drawImage(it.source, r.x, r.y);
    }

    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, atlas);
    this.atlasSize = { w: atlas.width, h: atlas.height };
    this.atlas = atlas;
    this.atlasVersion++;

    this.resetKinds();
    this.white = this.rects.get('gen:white') ?? { x: 0, y: 0, w: 1, h: 1 };
    this.telegraphs = [];
    for (let r = 3; r <= TELEGRAPH_RADIUS; r++) this.telegraphs[r] = this.rects.get(`gen:telegraph:${r}`) ?? this.white;
    this.hitRings = [];
    for (let r = 1; r <= MAX_HIT_RING; r++) this.hitRings[r] = this.rects.get(`gen:hit:${r}`) ?? this.white;
    this.playerRing = this.rects.get('gen:hit:player') ?? null;
    this.stuckMark = this.rects.get('gen:stuck') ?? this.white;
    this.stuckRing = this.rects.get('gen:stuck:ship') ?? this.white;
    this.hitBands = [];
    for (let w = 2; w <= MAX_HIT_BAND; w++) this.hitBands[w] = this.rects.get(`gen:hit:band:${w}`) ?? this.white;
    this.itemLooks = [
      this.resolve(kindSpec('coin'), circleKey(kindSpec('coin'))),
      this.resolve(kindSpec('gem'), circleKey(kindSpec('gem'))),
    ];
    this.shipLook = this.resolve(ship, 'gen:ship');
    this.hitbox = this.rects.get('gen:hitbox') ?? { x: 0, y: 0, w: 1, h: 1 };
    this.effectLooks = { hit: this.resolve(effects.hit, null), explosion: this.resolve(effects.explosion, null) };
  }

  /** Maps a laser kind to atlas rectangles, falling back to a generated solid beam. */
  private resolveLaser(spec: LaserSpec): LaserLook {
    const rgb = hexRgb(spec.color);
    const strip = (file: string, w: number, h: number, frames: number) => {
      const r = this.rects.get(file);
      return r ? Array.from({ length: frames }, (_, i) => ({ x: r.x + i * w, y: r.y, w, h })) : [];
    };
    const frames: Rect[] = [];
    for (const f of spec.frames) {
      const r = this.rects.get(f.file);
      if (!r) break;
      frames.push({ x: r.x + f.sx, y: r.y + f.sy, w: spec.w, h: spec.h });
    }
    if (frames.length === spec.frames.length && frames.length > 0) {
      return {
        spec,
        frames,
        muzzle: spec.muzzle ? strip(spec.muzzle.file, spec.muzzle.w, spec.muzzle.h, spec.muzzle.frames) : [],
        hit: spec.hit ? strip(spec.hit.file, spec.hit.w, spec.hit.h, spec.hit.frames) : [],
        rgb,
      };
    }
    const solid = this.rects.get(beamKey(spec.color)) ?? this.white;
    return { spec, frames: [solid], muzzle: [], hit: [], rgb };
  }

  /**
   * Maps a sprite spec to atlas rectangles at field resolution. If any frame is missing it uses
   * the generated stand-in `fallback` instead, or hides the sprite when there's none.
   */
  private resolve(spec: KindSpec, fallback: string | null): Look {
    const frames: Rect[] = [];
    const k = spec.scale;
    for (const f of spec.frames) {
      const file = this.rects.get(k === 1 ? f.file : shrunkKey(f.file, k));
      if (!file) break;
      frames.push({
        x: file.x + Math.round(f.sx * k),
        y: file.y,
        w: Math.round(spec.w * k),
        h: Math.round(spec.h * k),
      });
    }
    if (frames.length > 0 && frames.length === spec.frames.length) {
      return { frames, fps: spec.fps, angle: spec.angle, spin: spec.spin, hidden: spec.hidden };
    }
    const stand = fallback ? this.rects.get(fallback) : undefined;
    return { frames: stand ? [stand] : [], fps: 0, angle: null, spin: 0, hidden: spec.hidden || !stand };
  }
}

function shrunkKey(file: string, scale: number): string {
  return `${file}@${scale}`;
}

/** Composed frames whose layers all loaded. */
function composeFrames(images: Map<string, HTMLImageElement>): Map<string, HTMLCanvasElement> {
  const out = new Map<string, HTMLCanvasElement>();
  for (const [key, comp] of Object.entries(compositions)) {
    if (!comp.layers.every((l) => images.has(l.file))) continue;
    const [c, g] = canvas2d(comp.w, comp.h);
    if (comp.flipY) g.setTransform(1, 0, 0, -1, 0, comp.h);
    for (const l of comp.layers) g.drawImage(images.get(l.file) as HTMLImageElement, l.x, l.y);
    out.set(key, c);
  }
  return out;
}

/** Smoothly shrunk copies of every sprite a kind draws below 1:1. */
function shrunkCopies(images: Map<string, HTMLImageElement | HTMLCanvasElement>): Map<string, HTMLCanvasElement> {
  const out = new Map<string, HTMLCanvasElement>();
  for (const spec of Object.values(kinds)) {
    if (spec.scale === 1) continue;
    for (const f of spec.frames) {
      const key = shrunkKey(f.file, spec.scale);
      const img = images.get(f.file);
      if (!img || out.has(key)) continue;
      const [c, g] = canvas2d(Math.round(img.width * spec.scale), Math.round(img.height * spec.scale));
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = 'high';
      g.drawImage(img, 0, 0, c.width, c.height);
      out.set(key, c);
    }
  }
  return out;
}

/** Big things (enemies, bosses) fall back to rings so they don't bury the bullets. */
function circleKey(spec: KindSpec): string {
  const ring = spec.radius > 12;
  const r = ring ? Math.min(spec.radius, 32) : spec.radius;
  return `gen:${ring ? 'ring' : 'disc'}:${r}:${spec.color}`;
}

/** Sprites drawn in code: a circle for every kind's fallback, the ship and the hitbox. */
function standIns(): Map<string, HTMLCanvasElement> {
  const out = new Map<string, HTMLCanvasElement>();
  for (const spec of Object.values(kinds)) {
    const key = circleKey(spec);
    if (out.has(key)) continue;
    const [, style, radius, color] = key.split(':');
    out.set(key, pixelCircle(Number(radius), color, style === 'ring'));
  }
  out.set('gen:hitbox', hitboxSprite());
  out.set('gen:white', solidSprite('#ffffff'));
  for (let r = 3; r <= TELEGRAPH_RADIUS; r++) out.set(`gen:telegraph:${r}`, ringSprite(r, '#ffffff'));
  for (let r = 1; r <= MAX_HIT_RING; r++) out.set(`gen:hit:${r}`, hitSprite(r, HIT_COLOR));
  out.set('gen:hit:player', hitSprite(Math.round(PLAYER_HIT), '#ff4fc3'));
  for (let w = 2; w <= MAX_HIT_BAND; w++) out.set(`gen:hit:band:${w}`, hitBandSprite(w));
  out.set('gen:stuck', ringSprite(4, '#ff4a4a'));
  out.set('gen:stuck:ship', ringSprite(12, '#ff4a4a'));
  for (const spec of Object.values(laserKinds)) {
    if (!out.has(beamKey(spec.color))) out.set(beamKey(spec.color), beamSprite(spec.color));
  }
  out.set('gen:ship', shipSprite());
  return out;
}

function canvas2d(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  if (!g) throw new Error('Canvas 2D is not available');
  return [c, g];
}

/** A disc (or ring) drawn pixel by pixel. Discs get a pale core so they read as bullets. */
function pixelCircle(radius: number, color: string, ring: boolean): HTMLCanvasElement {
  const d = Math.ceil(radius * 2);
  const [c, g] = canvas2d(d, d);
  const mid = d / 2;
  for (let j = 0; j < d; j++) {
    for (let i = 0; i < d; i++) {
      const dist = Math.hypot(i + 0.5 - mid, j + 0.5 - mid);
      if (dist > radius || (ring && dist < radius - 2)) continue;
      g.fillStyle = !ring && dist < radius * 0.45 ? '#fff6fd' : color;
      g.fillRect(i, j, 1, 1);
    }
  }
  return c;
}

function beamKey(color: string): string {
  return `gen:beam:${color}`;
}

function hexRgb(color: string): [number, number, number] {
  const n = parseInt(color.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** A few pixels of one colour, for lines drawn through the laser pass. */
function solidSprite(color: string): HTMLCanvasElement {
  const [c, g] = canvas2d(4, 4);
  g.fillStyle = color;
  g.fillRect(0, 0, 4, 4);
  return c;
}

/** A beam without sprites: the colour at the edges, brightening to white down the middle. */
function beamSprite(color: string): HTMLCanvasElement {
  const [c, g] = canvas2d(9, 4);
  const [r, gr, b] = hexRgb(color);
  for (let i = 0; i < 9; i++) {
    const t = 1 - Math.abs(i - 4) / 4.5;
    const mix = (v: number) => Math.round(255 * (v + (1 - v) * Math.max(0, t - 0.45) * 1.6));
    g.fillStyle = `rgba(${mix(r)}, ${mix(gr)}, ${mix(b)}, ${Math.min(1, 0.35 + t)})`;
    g.fillRect(i, 0, 1, 4);
  }
  return c;
}

/** A one-pixel ring. */
function ringSprite(radius: number, color: string): HTMLCanvasElement {
  const d = radius * 2 + 1;
  const [c, g] = canvas2d(d, d);
  g.fillStyle = color;
  for (let j = 0; j < d; j++) {
    for (let i = 0; i < d; i++) {
      const dist = Math.hypot(i - radius, j - radius);
      if (Math.abs(dist - radius) < 0.5) g.fillRect(i, j, 1, 1);
    }
  }
  return c;
}

/** A hit circle: a one-pixel ring around a dark fill, so it reads over a sprite of any colour. */
function hitSprite(radius: number, color: string): HTMLCanvasElement {
  const d = radius * 2 + 1;
  const [c, g] = canvas2d(d, d);
  for (let j = 0; j < d; j++) {
    for (let i = 0; i < d; i++) {
      const dist = Math.hypot(i - radius, j - radius);
      if (dist > radius + 0.5) continue;
      const edge = dist > radius - 0.5;
      g.globalAlpha = edge ? 1 : 0.5;
      g.fillStyle = edge ? color : '#000000';
      g.fillRect(i, j, 1, 1);
    }
  }
  return c;
}

/** A slice across a laser's hit band, edges and all; the laser pass stretches it to the width. */
function hitBandSprite(width: number): HTMLCanvasElement {
  const [c, g] = canvas2d(width, 1);
  g.globalAlpha = 0.5;
  g.fillStyle = '#000000';
  g.fillRect(1, 0, width - 2, 1);
  g.globalAlpha = 1;
  g.fillStyle = HIT_COLOR;
  g.fillRect(0, 0, 1, 1);
  g.fillRect(width - 1, 0, 1, 1);
  return c;
}

function hitboxSprite(): HTMLCanvasElement {
  const [c, g] = canvas2d(5, 5);
  for (let j = 0; j < 5; j++) {
    for (let i = 0; i < 5; i++) {
      const dist = Math.hypot(i - 2, j - 2);
      if (dist > 2.4) continue;
      g.fillStyle = dist > 1.2 ? '#ff4fc3' : '#ffffff';
      g.fillRect(i, j, 1, 1);
    }
  }
  return c;
}

function shipSprite(): HTMLCanvasElement {
  const [c, g] = canvas2d(15, 17);
  g.fillStyle = '#c9c4e6';
  for (let j = 0; j < 17; j++) {
    const half = Math.floor((j / 16) * 7);
    g.fillRect(7 - half, j, half * 2 + 1, 1);
  }
  return c;
}
